import { useEffect, useReducer } from 'react';

import { useHistory, useLocation } from 'react-router';

import { getDeviceId } from 'helpers/deviceId';
import { selectDisableFaceAuthEmailPass } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';

import { ActionKind } from '../Common/interface';
import { getEmailValidation, getPasswordValidation } from './helpers';
import { ACTION_TYPE, LoginState, UseLogin } from './interface';
import reducers from './reducers';

const initializeState = (): LoginState => {
  return {
    email: {
      value: '',
      error: false,
      errorMessage: '',
    },
    emailDomailCorrection: {
      suggestion: false,
      suggestedEmail: '',
      suggestionCount: 0,
    },
    password: {
      value: '',
      error: false,
      errorMessage: '',
    },
    defaultErrorMessage: '',
    recaptchaError: false,
    isLoginBlocked: false,
    showLoader: false,
    showOAuthBtnLoader: false,
    pre2faToken: '',
    deviceId: getDeviceId(),
    verificationVariant: null,
    code: null,
    phoneNumber: '',
    phoneNumberAdded: false,
    phoneOTPRequired: false,
    isComplianceModalOpen: false,
    oAuth: {
      error: false,
      provider: 'google',
      errorMessage: '',
      errorCode: '',
    },
    country: '',
    isOneTapLogin: false,
  };
};

function reducer(state: LoginState, action: ACTION_TYPE) {
  const { type, payload } = action;
  switch (type) {
    case ActionKind.SetEmail:
      return {
        ...state,
        email: {
          ...state.email,
          value: payload,
          ...getEmailValidation(payload),
        },
        defaultErrorMessage: '',
        isLoginBlocked: false,
      };

    case ActionKind.SetEmailDomainCorrection:
      return reducers.setEmailDomailCorrection(state, payload);

    case ActionKind.SetPassword:
      return {
        ...state,
        password: {
          ...state.password,
          value: payload,
          ...getPasswordValidation(payload),
        },
      };

    case ActionKind.SetDefaultError:
      return {
        ...state,
        defaultErrorMessage: payload,
      };

    case ActionKind.SetLoader:
      return {
        ...state,
        showLoader: payload,
      };

    case ActionKind.SetOAuthBtnLoader:
      return {
        ...state,
        showOAuthBtnLoader: payload,
      };

    case ActionKind.setComplianceModal:
      return {
        ...state,
        isComplianceModalOpen: action.payload,
      };

    case ActionKind.RecaptchaError:
      return reducers.recaptchaError(state);

    case ActionKind.SubmitClick:
      return reducers.submitClick(state, payload);

    case ActionKind.OAuthLoginInit:
      return reducers.oAuthLoginInit(state, payload);

    case ActionKind.OAuthLoginError:
      return reducers.oAuthLoginError(state, payload);

    case ActionKind.Login_Pre2FA_Success:
      return reducers.loginPre2FASuccess(state, payload);

    case ActionKind.Login_Pre2FA_Failure:
      return reducers.loginPre2FAFailure(state, payload);

    case ActionKind.ResetLogin:
      return initializeState();

    default:
      return state;
  }
}

const useLogin = (): UseLogin => {
  const { requestedEmail, requestedPasswordBase64 } = useAppSelector(
    selectDisableFaceAuthEmailPass
  );
  const location = useLocation();
  const isOneTapLogin = location.state?.isOneTapLogin || false;
  const history = useHistory();

  const [state, dispatch] = useReducer(reducer, {
    ...initializeState(),
    ...(location.state?.loginState ?? {}),
    isOneTapLogin,
    email: {
      value: requestedEmail,
      error: false,
      errorMessage: '',
    },
    password: {
      value: atob(requestedPasswordBase64),
      error: false,
      errorMessage: '',
    },
  });

  useEffect(() => {
    /**
     * We are sending error info from google one tap in location state, so here we are just clearing location state after mount.
     * If we don't do this, the location state gets preserved even after a page refresh and same error is shown again on the screen.
     */
    if (
      typeof location.state === 'object' &&
      (location.state?.loginState || location.state?.isOneTapLogin)
    ) {
      const newState = { ...location.state };
      delete newState.loginState;
      delete newState.isOneTapLogin;
      history.replace({ ...location, state: newState });
    }
  }, [history, location]);

  // console.log('DEBUG: useLogin', state);

  return { state, dispatch };
};

export { useLogin };
